/**
 * /tools/weekly-review-v2?clientId=X — NL-041 (Step 8).
 *
 * Structured 3-prompt weekly reflection: "what worked", "what didn't",
 * "what's the focus next week". Persists to the new `weekly_reviews`
 * reflection_* columns via PUT /clients/:id/weekly-review-v2/:weekStart,
 * then submits a thin tool_completion row so the catalog reflects a
 * finalized tool.
 *
 * Distinct from the older `/tools/weekly-review` (STP-042 — the multi-
 * section template that persists to `client_documents`). The v2 tool is
 * the new lightweight cadence the NL-043 Step 8 popup reads from.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

/**
 * Monday of the ISO week containing `date` — mirror of the server's
 * helper. Returns YYYY-MM-DD. ISO weeks start on Monday.
 */
function isoWeekStart(date: Date): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}

interface FormState {
  good: string;
  notGood: string;
  nextFocus: string;
}

const DEFAULT_FORM: FormState = { good: '', notGood: '', nextFocus: '' };

export function WeeklyReviewV2Page(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();
  const weekStart = isoWeekStart(new Date());

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const current = await api.getWeeklyReviewV2(accessToken, clientId, { weekStart });
        if (!active) return;
        if (current) {
          setForm({
            good: current.reflectionGood ?? '',
            notGood: current.reflectionNotGood ?? '',
            nextFocus: current.reflectionNextFocus ?? '',
          });
        }
      } catch {
        /* swallow — fresh form. */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, weekStart]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.weeklyReviewV2.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const good = form.good.trim();
    const notGood = form.notGood.trim();
    const nextFocus = form.nextFocus.trim();
    try {
      // 1. Persist the three reflection columns on weekly_reviews.
      await api.setWeeklyReviewV2(accessToken, clientId, weekStart, {
        reflectionGood: good.length > 0 ? good : null,
        reflectionNotGood: notGood.length > 0 ? notGood : null,
        reflectionNextFocus: nextFocus.length > 0 ? nextFocus : null,
      });
      // 2. Thin tool-completion marker so the catalog + journey popup
      //    framework see a finalized row.
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'weekly-review-v2',
        {
          payload: {
            weekStart,
            snapshot: {
              good: good.length > 0 ? good : null,
              notGood: notGood.length > 0 ? notGood : null,
              nextFocus: nextFocus.length > 0 ? nextFocus : null,
            },
          },
        },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.weeklyReviewV2.errors.generic'));
      } else {
        setSubmitError(t('tools.weeklyReviewV2.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="weekly-review-v2-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.weeklyReviewV2.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">{t('tools.weeklyReviewV2.errors.missingClient')}</p>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="weekly-review-v2-loading">
        <p className="text-gray-2">{t('common.loading')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.weeklyReviewV2.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.weeklyReviewV2.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.weeklyReviewV2.description')}
        </p>
        <p className="mt-s2 font-mono text-xs text-gray-1">
          {t('tools.weeklyReviewV2.weekOf', { date: weekStart })}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.weeklyReviewV2.fields.good')}</span>
            <textarea
              value={form.good}
              onChange={(e) => setForm((prev) => ({ ...prev, good: e.target.value }))}
              placeholder={t('tools.weeklyReviewV2.placeholders.good')}
              rows={4}
              maxLength={4000}
              className="input-field mt-s2 w-full"
              data-testid="weekly-review-v2-good"
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.weeklyReviewV2.fields.notGood')}</span>
            <textarea
              value={form.notGood}
              onChange={(e) => setForm((prev) => ({ ...prev, notGood: e.target.value }))}
              placeholder={t('tools.weeklyReviewV2.placeholders.notGood')}
              rows={4}
              maxLength={4000}
              className="input-field mt-s2 w-full"
              data-testid="weekly-review-v2-not-good"
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.weeklyReviewV2.fields.nextFocus')}</span>
            <textarea
              value={form.nextFocus}
              onChange={(e) => setForm((prev) => ({ ...prev, nextFocus: e.target.value }))}
              placeholder={t('tools.weeklyReviewV2.placeholders.nextFocus')}
              rows={3}
              maxLength={4000}
              className="input-field mt-s2 w-full"
              data-testid="weekly-review-v2-next-focus"
            />
          </label>
        </article>

        <FinalizeWarning />

        {submitError && (
          <p className="form-error" role="alert" data-testid="weekly-review-v2-error">
            {submitError}
          </p>
        )}
        {savedAt && (
          <p className="text-flame-deep text-sm" role="status" data-testid="weekly-review-v2-saved">
            {t('tools.weeklyReviewV2.saved')}
          </p>
        )}

        <div className="flex justify-end gap-s3">
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary"
            data-testid="weekly-review-v2-submit"
          >
            {submitting
              ? t('tools.weeklyReviewV2.submitting')
              : t('tools.weeklyReviewV2.submit')}
          </button>
        </div>
      </form>
    </section>
  );
}
